import React from 'react';
import { Smartphone, Monitor, MapPin, Clock, PieChart } from 'lucide-react';

const AnalyticsTab = ({
  extendedStats,
  getDeviceIcon,
  getBrowserIcon,
  getBrowserColor
}) => {
  const { deviceDistribution, browserDistribution, topCities, hourlyTraffic } = extendedStats;

  const totalDevices = deviceDistribution?.reduce((acc, d) => acc + d.count, 0) || 1;
  const totalBrowsers = browserDistribution?.reduce((acc, b) => acc + b.count, 0) || 1;
  const maxHourlyCount = Math.max(...(hourlyTraffic?.map((h) => h.count) || [1]), 1);

  return (
    <div className="tab-pane analytics-pane">
      {/* Top Row: Device & Browser Distribution */}
      <div className="overview-grid-2col">
        {/* Device Distribution */}
        <div className="cyber-panel">
          <div className="panel-header">
            <div className="panel-title-group">
              <Smartphone className="panel-icon cyan" />
              <h3>DEVICE TYPE BREAKDOWN</h3>
            </div>
            <span className="panel-sub-tag">CLIENT PLATFORMS</span>
          </div>

          <div className="panel-body">
            <div className="dist-list">
              {deviceDistribution?.map((dev, idx) => {
                const percent = Math.round((dev.count / totalDevices) * 100);

                return (
                  <div key={idx} className="dist-row">
                    <div className="dist-info">
                      <span className="dist-icon-symbol">{getDeviceIcon(dev._id)}</span>
                      <span className="dist-name">{dev._id}</span>
                      <span className="dist-count">{dev.count} ({percent}%)</span>
                    </div>
                    <div className="dist-bar-track">
                      <div className="dist-bar-fill cyan" style={{ width: `${percent}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Browser Distribution */}
        <div className="cyber-panel">
          <div className="panel-header">
            <div className="panel-title-group">
              <Monitor className="panel-icon purple" />
              <h3>BROWSER DISTRIBUTION</h3>
            </div>
            <span className="panel-sub-tag">USER AGENTS</span>
          </div>

          <div className="panel-body">
            <div className="dist-list">
              {browserDistribution?.map((b, idx) => {
                const percent = Math.round((b.count / totalBrowsers) * 100);
                const bColor = getBrowserColor(b._id);

                return (
                  <div key={idx} className="dist-row">
                    <div className="dist-info">
                      <span className="dist-icon-symbol">{getBrowserIcon(b._id)}</span>
                      <span className="dist-name">{b._id}</span>
                      <span className="dist-count">{b.count} ({percent}%)</span>
                    </div>
                    <div className="dist-bar-track">
                      <div
                        className="dist-bar-fill"
                        style={{ width: `${percent}%`, backgroundColor: bColor }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Top Cities & 24-Hour Traffic Curve */}
      <div className="overview-grid-2col" style={{ marginTop: '24px' }}>
        {/* Top Cities */}
        <div className="cyber-panel">
          <div className="panel-header">
            <div className="panel-title-group">
              <MapPin className="panel-icon green" />
              <h3>TOP VISITOR CITIES</h3>
            </div>
            <span className="panel-sub-tag">METROPOLITAN NODES</span>
          </div>

          <div className="panel-body">
            <div className="rank-list">
              {topCities?.slice(0, 6).map((city, idx) => (
                <div key={idx} className="rank-item">
                  <div className="rank-left">
                    <span className="rank-badge">#{idx + 1}</span>
                    <span className="rank-name">{city._id || 'Unknown City'}</span>
                  </div>
                  <div className="rank-right">
                    <span className="rank-count green">{city.count} visits</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hourly Traffic Distribution */}
        <div className="cyber-panel">
          <div className="panel-header">
            <div className="panel-title-group">
              <Clock className="panel-icon pink" />
              <h3>HOURLY TRAFFIC PATTERN (24H)</h3>
            </div>
            <span className="panel-sub-tag">PEAK HOURS</span>
          </div>

          <div className="panel-body">
            <div className="hourly-sparkline">
              {hourlyTraffic?.map((hour, idx) => {
                const heightPercent = Math.round((hour.count / maxHourlyCount) * 100);
                const displayHour = hour._id >= 12 
                  ? `${hour._id === 12 ? 12 : hour._id - 12}P` 
                  : `${hour._id === 0 ? 12 : hour._id}A`;

                return (
                  <div key={idx} className="spark-col" title={`${displayHour}: ${hour.count} visits`}>
                    <div className="spark-bar-track">
                      <div
                        className="spark-bar-fill"
                        style={{ height: `${Math.max(heightPercent, 10)}%` }}
                      ></div>
                    </div>
                    <span className="spark-lbl">{displayHour}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
